import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { Mail, Phone, MapPin, Linkedin, Send } from 'lucide-react';
import { profileData } from '../mock/mockData';
import { contactAPI, analyticsAPI } from '../utils/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await contactAPI.submit(formData);
      analyticsAPI.trackEvent('contact', formData.email);
      toast({
        title: "Message Sent!",
        description: "Thank you for your message. I'll get back to you soon.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast({
        title: "Failed to Send",
        description: "Something went wrong. Please try again or email directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-16 px-6">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">Get in Touch</h1>
          <p className="text-xl text-gray-300">Let's discuss how we can collaborate</p>
          <div className="h-1 w-32 bg-gradient-to-r from-blue-600 to-sky-500 mx-auto rounded-full mt-6"></div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6 border-blue-900/30 shadow-xl bg-gradient-to-br from-blue-950/40 to-sky-950/40 backdrop-blur">
              <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-900/40 rounded-full flex-shrink-0">
                    <Mail className="h-5 w-5 text-sky-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-300 mb-1">Email</p>
                    <a href={`mailto:${profileData.email}`} className="text-white font-medium hover:text-sky-300 transition-colors">
                      {profileData.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-900/40 rounded-full flex-shrink-0">
                    <Phone className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-300 mb-1">Phone</p>
                    <a href={`tel:${profileData.phone}`} className="text-white font-medium hover:text-sky-300 transition-colors">
                      {profileData.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-900/40 rounded-full flex-shrink-0">
                    <MapPin className="h-5 w-5 text-sky-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-300 mb-1">Location</p>
                    <p className="text-white font-medium">{profileData.location}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-900/40 rounded-full flex-shrink-0">
                    <Linkedin className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-300 mb-1">LinkedIn</p>
                    <a
                      href={profileData.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white font-medium hover:text-sky-300 transition-colors"
                    >
                      View Profile
                    </a>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-blue-900/30 shadow-xl bg-[#060f1e]/80 backdrop-blur">
              <h3 className="text-lg font-bold text-white mb-3">Business Hours</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">Sunday - Thursday</span>
                  <span className="text-white font-medium">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Friday - Saturday</span>
                  <span className="text-white font-medium">Closed</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-4">GST (Gulf Standard Time)</p>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <Card className="p-8 border-blue-900/30 shadow-xl bg-[#060f1e]/80 backdrop-blur">
              <h2 className="text-2xl font-bold text-white mb-6">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="w-full bg-blue-950/20 border-blue-900/30 text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className="w-full bg-blue-950/20 border-blue-900/30 text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                    Subject *
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="What is this regarding?"
                    className="w-full bg-blue-950/20 border-blue-900/30 text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell me about your inquiry..."
                    rows={6}
                    className="w-full bg-blue-950/20 border-blue-900/30 text-white placeholder:text-gray-500"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;